/* Decohome theme JS — vanilla, no deps. Handles:
   - Mobile menu open/close
   - Cart drawer open/close
   - Quick-add to cart (no page reload)
   - Cart line qty +/- and remove (in drawer)
   - Preserve preview_theme_id across navigations
   - Sticky PDP bar visibility
   - Fade-in observer for editorial sections
*/
(function () {
  'use strict';

  console.log("DECOHOME theme v1.0 loaded");

  const root = document.documentElement;
  const $ = (s, r) => (r || document).querySelector(s);
  const $$ = (s, r) => Array.from((r || document).querySelectorAll(s));

  // ---- Preserve ?preview_theme_id across links ----
  function preservePreview(url) {
    try {
      const p = new URLSearchParams(window.location.search).get('preview_theme_id');
      if (!p) return url;
      const u = new URL(url, window.location.origin);
      if (u.origin === window.location.origin) u.searchParams.set('preview_theme_id', p);
      return u.pathname + u.search + u.hash;
    } catch (e) { return url; }
  }
  function bindPreservePreview() {
    document.addEventListener('click', (e) => {
      const a = e.target.closest('a[data-preserve-preview]');
      if (!a) return;
      const orig = a.getAttribute('href') || '/';
      a.setAttribute('href', preservePreview(orig));
    }, true);
  }

  // ---- Mobile menu + cart drawer ----
  // Toggle classes on BOTH <html> AND the target element so either CSS
  // selector pattern (html.menu-open OR .mobile-menu.on) works.
  // iOS/Android-safe scroll lock: body{overflow:hidden} does NOT stop touch
  // scrolling on mobile, which makes taps inside an overlay scroll the page
  // and miss their target. Pin the body with position:fixed and restore on close.
  let savedScrollY = 0;
  function lockScroll() {
    if (document.body.hasAttribute('data-scroll-locked')) return;
    savedScrollY = window.scrollY || window.pageYOffset || 0;
    document.body.style.position = 'fixed';
    document.body.style.top = '-' + savedScrollY + 'px';
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    document.body.setAttribute('data-scroll-locked', '');
  }
  function unlockScroll() {
    // keep locked while any overlay is still open
    if (root.classList.contains('menu-open') || root.classList.contains('cart-open')) return;
    if (!document.body.hasAttribute('data-scroll-locked')) return;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    document.body.removeAttribute('data-scroll-locked');
    window.scrollTo(0, savedScrollY);
  }
  function openMenu() {
    root.classList.add('menu-open');
    $('.mobile-menu')?.classList.add('on');
    $('.mobile-menu-scrim')?.classList.add('on');
    lockScroll();
  }
  function closeMenu() {
    root.classList.remove('menu-open');
    $('.mobile-menu')?.classList.remove('on');
    $('.mobile-menu-scrim')?.classList.remove('on');
    unlockScroll();
  }
  function openCart() {
    root.classList.add('cart-open');
    $('.drawer')?.classList.add('on');
    $('.drawer-scrim')?.classList.add('on');
    lockScroll();
  }
  function closeCart() {
    root.classList.remove('cart-open');
    $('.drawer')?.classList.remove('on');
    $('.drawer-scrim')?.classList.remove('on');
    unlockScroll();
  }
  function bindOverlayControls() {
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-menu-toggle]')) {
        e.preventDefault();
        if (root.classList.contains('menu-open')) closeMenu(); else openMenu();
        return;
      }
      if (e.target.closest('[data-cart-toggle]')) {
        e.preventDefault();
        if (root.classList.contains('cart-open')) closeCart(); else openCart();
        return;
      }
      if (e.target.closest('[data-close-overlay]')) { closeMenu(); closeCart(); return; }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { closeMenu(); closeCart(); }
    });
  }

  // ---- Cart utilities ----
  async function getCart() {
    const r = await fetch('/cart.js', { credentials: 'same-origin' });
    return r.json();
  }
  function updateCartCounts(count) {
    $$('[data-cart-count]').forEach(el => {
      el.textContent = count;
      el.toggleAttribute('data-empty', count === 0);
    });
  }
  async function refreshCartDrawer() {
    // Re-render the cart drawer section against the current cart state.
    // Use the current page URL so we don't break if "/" has any issue.
    try {
      const here = window.location.pathname + window.location.search;
      const sep = here.includes('?') ? '&' : '?';
      const url = here + sep + 'sections=cart-drawer';
      const r = await fetch(url, { credentials: 'same-origin' });
      if (r.ok) {
        const data = await r.json();
        const html = data['cart-drawer'];
        if (html) {
          const tmp = document.createElement('div');
          tmp.innerHTML = html;
          // The section response wraps the drawer in <div id="shopify-section-cart-drawer">.
          // Find both the scrim and the drawer element to swap.
          const newScrim = tmp.querySelector('.drawer-scrim');
          const newDrawer = tmp.querySelector('.drawer');
          const oldScrim = $('.drawer-scrim');
          const oldDrawer = $('.drawer');
          if (newScrim && oldScrim) oldScrim.replaceWith(newScrim);
          if (newDrawer && oldDrawer) {
            const wasOpen = oldDrawer.classList.contains('on');
            oldDrawer.replaceWith(newDrawer);
            if (wasOpen) newDrawer.classList.add('on');
          }
        }
      }
    } catch (e) { console.error('cart drawer refresh failed', e); }
  }
  function showFlashError(msg) {
    const el = document.createElement('div');
    el.className = 'flash-toast';
    el.textContent = msg;
    document.body.appendChild(el);
    requestAnimationFrame(() => el.classList.add('on'));
    setTimeout(() => { el.classList.remove('on'); setTimeout(() => el.remove(), 350); }, 2400);
  }

  // ---- Add to cart (quick-add from card and PDP form) ----
  function bindAddToCart() {
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('form[action*="/cart/add"], form[data-ajax-cart]');
      if (!form) return;
      e.preventDefault();
      const submitBtn = form.querySelector('[type="submit"]');
      if (submitBtn) { submitBtn.dataset.busy = '1'; submitBtn.disabled = true; }
      try {
        const fd = new FormData(form);
        const r = await fetch('/cart/add.js', {
          method: 'POST',
          body: fd,
          headers: { 'Accept': 'application/json' },
          credentials: 'same-origin',
        });
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          showFlashError(err.description || 'Could not add to cart');
          return;
        }
        await r.json();
        const cart = await getCart();
        updateCartCounts(cart.item_count);
        await refreshCartDrawer();
        openCart();
      } catch (err) {
        showFlashError('Network error');
        console.error(err);
      } finally {
        if (submitBtn) { delete submitBtn.dataset.busy; submitBtn.disabled = false; }
      }
    });
  }

  // ---- Cart drawer line item controls (+/-, remove) ----
  async function changeLine(line, quantity) {
    try {
      const r = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ line, quantity }),
        credentials: 'same-origin',
      });
      const cart = await r.json();
      updateCartCounts(cart.item_count);
      await refreshCartDrawer();
    } catch (e) { console.error(e); }
  }
  function bindCartLineControls() {
    document.addEventListener('click', (e) => {
      const inc = e.target.closest('[data-line-inc]');
      const dec = e.target.closest('[data-line-dec]');
      const rm = e.target.closest('[data-line-remove]');
      if (inc) {
        e.preventDefault();
        const line = parseInt(inc.getAttribute('data-line-inc'), 10);
        const cur = parseInt(inc.getAttribute('data-current-qty'), 10) || 1;
        changeLine(line, cur + 1);
      } else if (dec) {
        e.preventDefault();
        const line = parseInt(dec.getAttribute('data-line-dec'), 10);
        const cur = parseInt(dec.getAttribute('data-current-qty'), 10) || 1;
        changeLine(line, Math.max(0, cur - 1));
      } else if (rm) {
        e.preventDefault();
        const line = parseInt(rm.getAttribute('data-line-remove'), 10);
        changeLine(line, 0);
      }
    });
  }

  // ---- PDP quantity stepper ----
  function bindPdpQty() {
    document.addEventListener('click', (e) => {
      const inc = e.target.closest('[data-qty-inc]');
      const dec = e.target.closest('[data-qty-dec]');
      if (!inc && !dec) return;
      e.preventDefault();
      const input = $('#Quantity') || $('input[name="quantity"]');
      if (!input) return;
      const cur = parseInt(input.value, 10) || 1;
      input.value = inc ? cur + 1 : Math.max(1, cur - 1);
    });
  }

  // ---- Fade-in observer (defensive — never leaves content invisible) ----
  function bindFadeIn() {
    const els = $$('.fade-in');
    if (!els.length) return;
    // Above-the-fold: show immediately
    els.forEach(el => {
      const r = el.getBoundingClientRect();
      if (r.top < window.innerHeight && r.bottom > 0) el.classList.add('seen');
    });
    if (!('IntersectionObserver' in window)) {
      els.forEach(el => el.classList.add('seen'));
      return;
    }
    const obs = new IntersectionObserver(entries => {
      entries.forEach(en => {
        if (en.isIntersecting) { en.target.classList.add('seen'); obs.unobserve(en.target); }
      });
    }, { threshold: 0, rootMargin: '0px 0px -10% 0px' });
    els.forEach(el => obs.observe(el));
    // Failsafe: force-show anything still hidden after 1.5s
    setTimeout(() => $$('.fade-in:not(.seen)').forEach(el => el.classList.add('seen')), 1500);
  }

  // ---- Sticky PDP add-to-cart on mobile ----
  function bindStickyPdp() {
    const bar = $('.pdp-sticky');
    const trigger = $('.product-form button[type="submit"]');
    if (!bar || !trigger) return;
    const obs = new IntersectionObserver(entries => {
      entries.forEach(en => bar.classList.toggle('on', !en.isIntersecting));
    }, { threshold: 0 });
    obs.observe(trigger);
  }

  // ---- Announcement ticker (fade-rotate messages) ----
  function bindTicker() {
    const ticker = $('[data-ticker]');
    if (!ticker) return;
    const msgs = $$('[data-ticker-msg]', ticker);
    if (msgs.length < 2) return; // single message: nothing to rotate
    let i = 0;
    setInterval(() => {
      msgs[i].classList.remove('is-on');
      i = (i + 1) % msgs.length;
      msgs[i].classList.add('is-on');
    }, 4000);
  }

  // ---- Boot ----
  function init() {
    bindPreservePreview();
    bindOverlayControls();
    bindAddToCart();
    bindCartLineControls();
    bindPdpQty();
    bindFadeIn();
    bindStickyPdp();
    bindTicker();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  window.Decohome = { openCart, closeCart, openMenu, closeMenu, refreshCartDrawer };
})();
